import React, { useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import dayjs from "dayjs"
import { useDispatch } from "react-redux"
import { DayPicker } from "react-day-picker"
import "react-day-picker/style.css"
import { Button, Flex } from "antd"
import { useTranslation } from "react-i18next"
import { Icon } from "ui/Icon"
import { fetchExperiencesPreviewByGuide } from "store/experiences"
import WidgetSheet from "../WidgetSheet"
import CalendarHeader from "../WidgetExperience/BookingPanel/CalendarHeader"
import calendarStyles from "../WidgetExperience/BookingPanel/BookingPanel.module.css"
import { toListDateParam } from "../helpers"
import { MAX_SEARCH_GUESTS, MIN_SEARCH_GUESTS } from "../constants"
import styles from "./WidgetListing.module.css"

const HiddenCaption = () => null
const CALENDAR_COMPONENTS = { MonthCaption: HiddenCaption }
const isPastDay = (day) => dayjs(day).isBefore(dayjs(), "day")

// The phone's search (Figma 6058:140316): tapping When or Guests opens this sheet. The date and
// guest count are edited here and only applied with "Show results (N)", whose count is the API's
// for the chosen date and guest count (asked again whenever either changes). "Clear all" empties
// both; the X closes without applying.
const FiltersSheet = ({ guideUuid, isOpen, isSearching, date, guests, onClose, onApply }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [draftDate, setDraftDate] = useState(date)
  const [draftGuests, setDraftGuests] = useState(guests)
  const [month, setMonth] = useState(() => (date || dayjs()).startOf("month").toDate())
  const [preview, setPreview] = useState(null)
  const [isCounting, setIsCounting] = useState(false)
  const requestRef = useRef(0)
  const dateParam = toListDateParam(draftDate)

  // Start from the values on the page each time the sheet opens.
  useEffect(() => {
    if (!isOpen) return
    setDraftDate(date)
    setDraftGuests(guests)
    setMonth((date || dayjs()).startOf("month").toDate())
  }, [isOpen, date, guests])

  useEffect(() => {
    if (!isOpen || !guideUuid) return
    const requestId = requestRef.current + 1
    requestRef.current = requestId
    setIsCounting(true)
    dispatch(fetchExperiencesPreviewByGuide(guideUuid, dateParam, draftGuests))
      .then((response) => requestRef.current === requestId && setPreview(response))
      .catch(() => requestRef.current === requestId && setPreview(null))
      .finally(() => requestRef.current === requestId && setIsCounting(false))
  }, [isOpen, guideUuid, dateParam, draftGuests, dispatch])

  const resultCount = preview?.metadata?.total_results ?? preview?.data?.length
  const hasCount = !isCounting && resultCount != null
  const counterValue = draftGuests || MIN_SEARCH_GUESTS

  const handleClearAll = () => {
    setDraftDate(null)
    setDraftGuests(null)
  }

  const footer = (
    <Flex gap={12}>
      <Button shape="round" color="primary" variant="outlined" className={styles.sheetButton} onClick={handleClearAll}>
        {t("booking_widget.search.clear_all")}
      </Button>
      <Button type="primary" shape="round" className={styles.sheetButton} loading={isCounting} disabled={isSearching} onClick={() => onApply({ date: draftDate, guests: draftGuests })}>
        {hasCount ? t("booking_widget.search.show_results", { count: resultCount }) : t("booking_widget.search.show_results_loading")}
      </Button>
    </Flex>
  )

  return (
    <WidgetSheet isOpen={isOpen} onClose={onClose} title={t("booking_widget.search.filters")} footer={footer}>
      <Flex vertical gap={8}>
        <span className={styles.sheetLabel}>{t("booking_widget.search.when")}</span>
        <div className={styles.sheetCalendar}>
          <CalendarHeader month={month} firstMonth={dayjs().startOf("month").toDate()} onMonthChange={setMonth}>
            <div className={calendarStyles.calendar}>
              <DayPicker
                mode="single"
                selected={draftDate ? draftDate.toDate() : undefined}
                onSelect={(day) => setDraftDate(day ? dayjs(day) : null)}
                disabled={isPastDay}
                month={month}
                onMonthChange={setMonth}
                hideNavigation
                components={CALENDAR_COMPONENTS}
              />
            </div>
          </CalendarHeader>
        </div>
        <Flex align="center" justify="space-between" gap={16} className={styles.sheetGuests}>
          <span className={styles.sheetLabel}>{t("booking_widget.search.number_of_guests")}</span>
          <Flex align="center" gap={8}>
            <Button
              shape="circle"
              className={styles.counterButton}
              aria-label={t("booking_widget.search.decrease_guests")}
              disabled={counterValue <= MIN_SEARCH_GUESTS}
              onClick={() => setDraftGuests(Math.max(MIN_SEARCH_GUESTS, counterValue - 1))}
              icon={<Icon type="feather" name="Minus" width={16} height={16} color="currentColor" />}
            />
            <span className={styles.sheetCounterValue}>{counterValue}</span>
            <Button
              shape="circle"
              className={styles.counterButton}
              aria-label={t("booking_widget.search.increase_guests")}
              disabled={counterValue >= MAX_SEARCH_GUESTS}
              onClick={() => setDraftGuests(Math.min(MAX_SEARCH_GUESTS, counterValue + 1))}
              icon={<Icon type="feather" name="Plus" width={16} height={16} color="currentColor" />}
            />
          </Flex>
        </Flex>
      </Flex>
    </WidgetSheet>
  )
}

FiltersSheet.propTypes = {
  guideUuid: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  isSearching: PropTypes.bool,
  date: PropTypes.object,
  guests: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired
}

FiltersSheet.defaultProps = {
  isSearching: false,
  date: null,
  guests: null
}

export default FiltersSheet
